import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { CouponsQueryKeys } from "./couponsQueryKeys";
import {
  getCouponIds,
  getCoupons,
  getCouponsSummaryKeyset,
  getCouponsSummaryPaginated,
} from "@/api/coupons";
import { DateRange } from "@/types/Date";
import { getApiDateFilterBackend } from "@/utils/date";
import { useUser } from "@/tanstackQuery/queries";

// Server-side paginated coupon history (admin coupon-list / active-coupons).
export interface PaginatedCouponsHistoryParams {
  page: number;
  pageSize: number;
  q?: string;
  matchId?: string;
  statusList?: string[];
  type?: string;
  category?: string;
  /** Sport of the coupon ("football" / "basketball"); passed straight through. */
  sportCategory?: string;
  dealerIds?: number[];
  subDealerIds?: number[];
  userIds?: number[];
  names?: string[];
  startDate?: string;
  endDate?: string;
  /** Whole-set ordering (server-side); omitted → created_at desc. */
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export const useCouponsHistoryPaginated = (
  params: PaginatedCouponsHistoryParams,
  options: { deleted?: boolean } = {}
) => {
  return useQuery({
    queryKey: [CouponsQueryKeys.HistoryPaginated, params, options],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      getCouponsSummaryPaginated({
        signal,
        params: { ...params, ...(options.deleted ? { deleted: true } : {}) },
      }),
  });
};

// Cursor (keyset) variant — constant-time at any page depth. `cursor`+`direction`
// instead of a page number; the response carries start/end cursors for prev/next.
export interface KeysetCouponsHistoryParams {
  pageSize: number;
  cursor?: string;
  direction: "first" | "next" | "prev" | "last";
  q?: string;
  matchId?: string;
  statusList?: string[];
  type?: string;
  category?: string;
  sportCategory?: string;
  dealerIds?: number[];
  subDealerIds?: number[];
  userIds?: number[];
  names?: string[];
  startDate?: string;
  endDate?: string;
}

export const useCouponsKeyset = (
  params: KeysetCouponsHistoryParams,
  options: { deleted?: boolean } = {}
) => {
  return useQuery({
    queryKey: [CouponsQueryKeys.Keyset, params, options],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      getCouponsSummaryKeyset({
        signal,
        params: { ...params, ...(options.deleted ? { deleted: true } : {}) },
      }),
  });
};

interface QueryParams {
  category?: string;
  type?: string;
  statusList?: string[];
  date?: Date | DateRange;
  subDealerUsernames?: string[];
  playerUsernames?: string[];
  dealerUsernames?: string[];
  dealerIds?: number[];
  subDealerIds?: number[];
  userIds?: number[];
  names?: string[];
}

interface Options {
  isQueryEnabled?: boolean;
  shouldRetry?: boolean;
  queryParams: QueryParams;
}

interface CouponsHistoryOptions {
  matchId?: string;
  q?: string;
}

const buildSearchParams = (
  options: CouponsHistoryOptions,
  base: Record<string, unknown> = {}
): Record<string, unknown> => {
  const params: Record<string, unknown> = { ...base };
  if (options.matchId) params.matchId = options.matchId;
  if (options.q) params.q = options.q;
  return params;
};

export const useCouponsHistory = (options: CouponsHistoryOptions = {}) => {
  const user = useUser();
  const { matchId, q } = options;

  return useQuery({
    queryKey: [CouponsQueryKeys.History, { matchId, q }],
    queryFn: ({ signal }) => {
      return getCoupons({ signal, params: buildSearchParams(options) });
    },
  });
};

export const useDeletedCouponsHistory = (
  options: CouponsHistoryOptions = {}
) => {
  const user = useUser();
  const { matchId, q } = options;

  return useQuery({
    queryKey: [CouponsQueryKeys.Trash, { matchId, q }],
    queryFn: ({ signal }) => {
      return getCoupons({
        signal,
        params: buildSearchParams(options, { deleted: true }),
      });
    },
  });
};

export const useCouponsIds = (options: Options) => {
  const user = useUser();
  const { queryParams, isQueryEnabled, shouldRetry } = options;

  return useQuery({
    queryKey: ["coupons", "search", queryParams],
    enabled: isQueryEnabled,
    retry: shouldRetry,
    queryFn: async ({ signal }) => {
      const { date, ...restParams } = queryParams;
      const params = {
        ...restParams,
        ...getApiDateFilterBackend(date),
      };
      return getCouponIds({ signal, params });
    },
  });
};
